import { inject, Injectable } from '@angular/core';
import { ApiService } from '../apiservice/apiservice.service';
import { ConfigService } from '../config/config.service';
import { BehaviorSubject, forkJoin, Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { SharedService } from '../shared/shared.service';
import { ApiResponse } from '../api-response.interface';
import { StorageService } from '../storage/storage.service';

export interface OpacSettings {
  asFiltersOptions: any;
  facetOptions: any;
  staffTabOptions: any;
  loginOptions: any;
  displayTargets: any;
  fullRecordOptions: any;
  resultOptions: any;
  searchOptions: any;
  webLinkOptions: any;
}

export interface FullRecordTab {
  id: number;
  name: string;
  staffLabel: string;
  patronGuestLabel: string;
}

interface IsPatronGuestAndStaff<T> {
  patronGuest: T;
  staff: T;
}

export interface FullRecordOptions {
  defaultTabId: IsPatronGuestAndStaff<number>;
  displayMARC: IsPatronGuestAndStaff<boolean>;
  displayMoreAboutThisTitle: IsPatronGuestAndStaff<boolean>;
  displaySyndeticsUnbound: IsPatronGuestAndStaff<boolean>;
  displayNovelist: IsPatronGuestAndStaff<boolean>;
  displayCLCD: IsPatronGuestAndStaff<boolean>;
  displayShelfBrowse: IsPatronGuestAndStaff<boolean>;
  displayGoodreadsReview: IsPatronGuestAndStaff<boolean>;
  detailTab: FullRecordTab;
  whereTab: FullRecordTab;
  marcTab: FullRecordTab;
  moreAboutThisTitleTab: FullRecordTab;
  goodreadsReviewTab: FullRecordTab;
  syndeticsUnboundTab: FullRecordTab;
  novelistTab: FullRecordTab;
  clcdTab: FullRecordTab;
  shelfBrowseTab: FullRecordTab;
  copyCatalogForStaff: boolean;
  lenderColumnLabel?: string;
  lenderContentsLabel?: string;
  displayCopyCataloging: boolean;
  displayILLLenderToPatronGuest: number;
}

export interface IFacetOptions {
  facetId: number;
  description: string;
  mappedDescription: string;
  display: boolean;
}

export interface Weblink {
  weblinkPageText: string;
  weblinkTabLabel: string;
  weblinkOnChooseResourceScreen: WebLinkOptions;
  weblinkOnSeparateTab: WebLinkOptions;
}

interface WebLinkOptions {
  guest: boolean;
  patron: boolean;
  staff: boolean;
}

export interface LoginConfig {
  emailText: string;
  libraryProfileKey: number;
  loginInstructionalText: string;
  passwordLabel: string;
  redirectToAdvancedSearch: boolean;
  reminderEmailContent: string;
  requireLoginBeforSearch: boolean;
  showForgotPasswordLink: boolean;
  showLibraryLookupBox: boolean;
  showPasswordPrompt: boolean;
  userNameLabel: string;
  whatTypeInstructionText: string;
  whatTypePatronLabel: string;
  whatTypeStaffLabel: string;
}

@Injectable()
export class OpacSettingsService {
  url = '';
  private _loginOptionsBS = new BehaviorSubject<LoginConfig>(null);
  fullRecordOptionsBS = new BehaviorSubject<any>(null);
  resultOptionsBS = new BehaviorSubject<any>(null);
  searchOptionsBS = new BehaviorSubject<any>(null);
  webLinkOptionsBS = new BehaviorSubject<any>(null);

  private _apiService = inject(ApiService);
  private _sharedService = inject(SharedService);
  private _configService = inject(ConfigService);
  private _storageService = inject(StorageService);

  get loginConfig(): LoginConfig | null {
    return this._loginOptionsBS.value;
  }

  setLoginConfig(value: LoginConfig): void {
    this._loginOptionsBS.next(value);
  }

  // ----------------------------------------------------------------------------------------------------------
  // -------------------------------- getters and setters ----------------------------------------------------
  // -----------------------------------------------------------------------------------------------------------

  setWebLinkOptionsBS(value) {
    this.webLinkOptionsBS.next(value);
  }

  getWebLinkOptionsBS() {
    return this.webLinkOptionsBS.asObservable();
  }

  setSearchOptionsBS(value) {
    this.searchOptionsBS.next(value);
  }

  getSearchOptionsBS() {
    return this.searchOptionsBS.asObservable();
  }

  setResultOptionsBS(value) {
    this.resultOptionsBS.next(value);
  }

  getResultOptionsBS() {
    return this.resultOptionsBS.asObservable();
  }

  getLoginOptions(): Observable<LoginConfig> {
    const url = this._configService.agApi + '/opacSettings/login';

    return this._apiService.get<ApiResponse<LoginConfig>>(url).pipe(
      map((response) => {
        if (response.status === '200 OK' && response.data) {
          this.setLoginConfig(response.data);
          this._storageService.setShowLibBox(this.loginConfig.showLibraryLookupBox);
          return response.data;
        }
      }),
      catchError(() => {
        return throwError(() => new Error('Failed to retrieve login configuration.'));
      }),
    );
  }

  // ----------------------------------------------------------------------------------------------------------
  // ------------------------------------------ API CALLS ------------------------------------------------------
  // -----------------------------------------------------------------------------------------------------------

  getDisplayTargets(): Observable<any> {
    this.url = this._configService.agApi + '/opacSettings/LoginLibraries';
    return this._apiService.get(this.url);
  }

  setDisplayTargets(data): Observable<any> {
    this.url = this._configService.agApi + '/opacSettings/LoginLibraries';
    return this._apiService.put(this.url, data);
  }

  getASFilterOptions(): Observable<any> {
    this.url = this._configService.agApi + '/opacSettings/filterSettings';
    return this._apiService.get(this.url);
  }

  updateAdvSearchFilterOptions(data): Observable<any> {
    this.url = this._configService.agApi + '/opacSettings/filterSettings';
    return this._apiService.put(this.url, data);
  }

  getFacetOptions(): Observable<any> {
    this.url = this._configService.agApi + '/opacSettings/FacetOption';
    return this._apiService.get(this.url);
  }

  updateFacetOptions(data): Observable<any> {
    this.url = this._configService.agApi + '/opacSettings/FacetOption';
    return this._apiService.put(this.url, data);
  }

  getFullRecordOptions(): Observable<any> {
    this.url = this._configService.agApi + '/opacSettings/fullrecord';
    return this._apiService.get(this.url);
  }

  getResultOptions(): Observable<any> {
    this.url = this._configService.agApi + '/opacSettings/result';
    return this._apiService.get(this.url);
  }

  fetchOpacResults(): Observable<any> {
    return this.getResultOptions().pipe(
      map((response) => {
        this._sharedService.setOpacResultsBS(response.data);
        return response.data;
      }),
    );
  }

  getSearchOptions(): Observable<any> {
    this.url = this._configService.agApi + '/opacSettings/search';
    return this._apiService.get(this.url);
  }

  private _getWebLinkOptions(): Observable<ApiResponse<Weblink>> {
    this.url = this._configService.agApi + '/opacSettings/weblink';
    return this._apiService.get(this.url);
  }

  loadWebLinkOptions(): Observable<ApiResponse<Weblink>> {
    return this._getWebLinkOptions().pipe(
      map((response) => {
        if (response.status === '200 OK') {
          if (response.data) {
            this.setWebLinkOptionsBS(response.data);
          }

          return response;
        }
      }),
    );
  }

  getStaffTabOptions(): Observable<any> {
    this.url = this._configService.agApi + '/opacSettings/stafftab';
    return this._apiService.get(this.url);
  }

  updateLoginOptions(data): Observable<any> {
    this.url = this._configService.agApi + '/opacSettings/login';
    return this._apiService.put(this.url, data);
  }

  updateFullRecordOptions(data): Observable<any> {
    this.url = this._configService.agApi + '/opacSettings/fullrecord';
    return this._apiService.put(this.url, data);
  }

  updateResultsOptions(data): Observable<any> {
    this.url = this._configService.agApi + '/opacSettings/result';
    return this._apiService.put(this.url, data);
  }

  updateSearchOptions(data): Observable<any> {
    this.url = this._configService.agApi + '/opacSettings/search';
    return this._apiService.put(this.url, data);
  }

  updateWebLinksOptions(data): Observable<any> {
    this.url = this._configService.agApi + '/opacSettings/weblink';
    return this._apiService.put(this.url, data);
  }

  updateStaffLocalTabs(data): Observable<any> {
    this.url = this._configService.agApi + '/opacSettings/localTab';
    return this._apiService.put(this.url, data);
  }

  updateStaffGlobalTabs(data): Observable<any> {
    this.url = this._configService.agApi + '/opacSettings/globalTab';
    return this._apiService.put(this.url, data);
  }

  useGlobalOptions(): Observable<any> {
    this.url = this._configService.agApi + '/opacSettings/useGlobal';
    return this._apiService.get(this.url);
  }

  fetchWebLinksSeperateTab(): Observable<any> {
    this.url = this._configService.searchApi + '/library/WeblinkTargets';
    return this._apiService.get(this.url);
  }

  getOpacSettingsObservable(): Observable<OpacSettings> {
    return forkJoin([
      this.getASFilterOptions(),
      this.getFacetOptions(),
      this.getStaffTabOptions(),
      this.getDisplayTargets(),
      this.getFullRecordOptions(),
      this.getResultOptions(),
      this.getSearchOptions(),
      this.loadWebLinkOptions(),
    ]).pipe(
      map((data: any[]) => {
        const settings: OpacSettings = {
          asFiltersOptions: null,
          facetOptions: null,
          staffTabOptions: null,
          loginOptions: null,
          displayTargets: null,
          fullRecordOptions: null,
          resultOptions: null,
          searchOptions: null,
          webLinkOptions: null,
        };
        if (data[0] && data[0].status === '200 OK') {
          settings.asFiltersOptions = data[0].data;
        }
        if (data[1] && data[1].status === '200 OK') {
          settings.facetOptions = data[1].data;
        }
        if (data[2] && data[2].status === '200 OK') {
          settings.staffTabOptions = data[2].data;
        }

        if (data[3] && data[3].status === '200 OK') {
          settings.displayTargets = data[3].data;
        }
        if (data[4] && data[4].status === '200 OK') {
          settings.fullRecordOptions = data[4].data;
        }
        if (data[5] && data[5].status === '200 OK') {
          settings.resultOptions = data[5].data;
        }
        if (data[6] && data[6].status === '200 OK') {
          settings.searchOptions = data[6].data;
        }
        if (data[7] && data[7].status === '200 OK') {
          settings.webLinkOptions = data[7].data;
        }
        return settings;
      }),
    );
  }
}
